/**
 * Shortcuts — interactieve tegels op CockpitHome.
 * Persistentie in PostgreSQL (via backend → AI Engine), per ingelogde gebruiker.
 * Klik op een tegel = open in nieuw tabblad.
 * Modes per shortcut: 'favicon' (auto van domein) of 'icon' (Material Symbol + kleur).
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, IconButton, Tooltip,
} from '@mui/material';
import {
  DndContext, DragOverlay, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, arrayMove, rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import {
  T, Icon, COLORS, ICONS, safeUrl, hostnameOf, ShortcutDialog, ShortcutVisual,
} from './shortcutsCommon';

const API = `${BACKEND_URL}/Api/shortcuts`;

export default function Shortcuts({ embedded = false, hideDefaults = false }) {
  const navigate = useNavigate();
  const [defaults, setDefaults] = useState([]); // admin-managed, locked
  const [items, setItems] = useState([]);       // personal, sortable
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [hoverId, setHoverId] = useState(null);
  const [faviconFailed, setFaviconFailed] = useState({});
  const [activeId, setActiveId] = useState(null);
  const reorderTimer = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await authFetch(`${API}`);
        if (!r.ok) throw new Error('fetch failed');
        const data = await r.json();
        if (!cancelled) {
          setDefaults(data.defaults || []);
          setItems(data.shortcuts || []);
        }
      } catch {
        if (!cancelled) toast.error('Kon shortcuts niet laden');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const openShortcut = useCallback((item) => {
    const url = safeUrl(item.url);
    if (!url) return;
    // Interne route (bv. /weekplan) → binnen de app navigeren; extern → nieuw tabblad
    // FE-11: '//evil.example' en '/\\evil' beginnen ook met '/' maar zijn externe (protocol-relatieve) URL's
    if (url.startsWith('/') && !url.startsWith('//') && !url.startsWith('/\\')) navigate(url);
    else window.open(url, '_blank', 'noopener,noreferrer');
  }, [navigate]);

  const persistReorder = useCallback((orderedIds) => {
    if (reorderTimer.current) clearTimeout(reorderTimer.current);
    reorderTimer.current = setTimeout(async () => {
      try {
        await authFetch(`${API}/reorder`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ordered_ids: orderedIds }),
        });
      } catch { toast.error('Volgorde niet opgeslagen'); }
    }, 300);
  }, []);

  const handleDragStart = (e) => setActiveId(e.active.id);
  const handleDragEnd = (e) => {
    setActiveId(null);
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setItems(prev => {
      const oldIdx = prev.findIndex(x => x.id === active.id);
      const newIdx = prev.findIndex(x => x.id === over.id);
      if (oldIdx < 0 || newIdx < 0) return prev;
      const next = arrayMove(prev, oldIdx, newIdx);
      persistReorder(next.map(x => x.id));
      return next;
    });
  };

  const activeItem = activeId ? items.find(x => x.id === activeId) : null;

  const startNew = () => setEditing({
    id: `new-${Date.now()}`, name: '', url: '', mode: 'favicon',
    color: COLORS[0], icon: ICONS[0], isNew: true,
  });

  const startEdit = (e, item) => {
    e.stopPropagation();
    setEditing({ ...item });
  };

  const remove = async (e, id) => {
    e.stopPropagation();
    const prev = items;
    setItems(p => p.filter(x => x.id !== id));
    try {
      const r = await authFetch(`${API}/${id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error();
    } catch {
      setItems(prev);
      toast.error('Verwijderen mislukt');
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    const name = editing.name?.trim() || hostnameOf(editing.url) || 'Shortcut';
    const url = editing.url?.trim();
    if (!url) return;
    const payload = {
      name, url,
      mode: editing.mode || 'favicon',
      color: editing.color || null,
      icon: editing.icon || null,
    };
    try {
      if (editing.isNew) {
        const r = await authFetch(`${API}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...payload, position: items.length }),
        });
        if (!r.ok) throw new Error();
        const data = await r.json();
        if (data.shortcut) setItems(prev => [...prev, data.shortcut]);
      } else {
        const r = await authFetch(`${API}/${editing.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!r.ok) throw new Error();
        const data = await r.json();
        if (data.shortcut) setItems(prev => prev.map(x => x.id === data.shortcut.id ? data.shortcut : x));
      }
      setEditing(null);
    } catch {
      toast.error('Opslaan mislukt');
    }
  };

  const cancelEdit = () => setEditing(null);

  const shownDefaults = hideDefaults ? [] : defaults;
  const totalCount = shownDefaults.length + items.length;

  return (
    <Box sx={{ mb: embedded ? 0 : { xs: 4, md: 6 } }}>
      {!embedded && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography sx={{ fontFamily: T.font, fontSize: '1.05rem', fontWeight: 700, color: T.text }}>
            Shortcuts
          </Typography>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', fontWeight: 600, color: T.outlineVariant, letterSpacing: '0.04em' }}>
            {loading ? 'laden…' : `${totalCount} ${totalCount === 1 ? 'link' : 'links'}`}
          </Typography>
        </Box>
      )}

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
        gap: { xs: 1.5, md: 2 },
      }}>
        {/* Locked admin defaults — always at the front (verbergbaar via widget-setting) */}
        {shownDefaults.map(item => (
          <LockedTile
            key={`def-${item.id}`}
            item={item}
            faviconFailed={!!faviconFailed[`def-${item.id}`]}
            onOpen={openShortcut}
            onFaviconError={() => setFaviconFailed(p => ({ ...p, [`def-${item.id}`]: true }))}
          />
        ))}

        {/* Personal sortable shortcuts */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setActiveId(null)}
        >
          <SortableContext items={items.map(i => i.id)} strategy={rectSortingStrategy}>
            {items.map(item => (
              <SortableTile
                key={item.id}
                item={item}
                isHovered={hoverId === item.id}
                faviconFailed={!!faviconFailed[item.id]}
                onHover={setHoverId}
                onOpen={openShortcut}
                onEdit={startEdit}
                onRemove={remove}
                onFaviconError={(id) => setFaviconFailed(p => ({ ...p, [id]: true }))}
                isDragging={activeId === item.id}
              />
            ))}
          </SortableContext>
          <DragOverlay>
            {activeItem ? <TilePresentation item={activeItem} dragging /> : null}
          </DragOverlay>
        </DndContext>

        {/* Add button */}
        <Box
          onClick={startNew}
          sx={{
            cursor: 'pointer',
            border: `1.5px dashed ${T.outlineVariant}`, borderRadius: '0.75rem', p: 2, minHeight: 64,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
            color: T.outline, transition: 'all 0.15s',
            '&:hover': { borderColor: T.primary, color: T.primary, bgcolor: 'rgba(82,96,117,0.04)' },
          }}
        >
          <Icon name="add" size={20} />
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 700 }}>
            Shortcut toevoegen
          </Typography>
        </Box>
      </Box>

      <ShortcutDialog editing={editing} onChange={setEditing} onSave={saveEdit} onCancel={cancelEdit} />
    </Box>
  );
}

function SortableTile({ item, isHovered, faviconFailed, onHover, onOpen, onEdit, onRemove, onFaviconError, isDragging }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging: sortableDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: sortableDragging || isDragging ? 0 : 1,
  };

  const host = hostnameOf(item.url);

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={(e) => {
        // Don't open if a click target is an action button
        if (e.defaultPrevented) return;
        onOpen(item);
      }}
      onMouseEnter={() => onHover(item.id)}
      onMouseLeave={() => onHover(null)}
      sx={{
        // Primaire actie is klikken → klik-cursor; pas tijdens het slepen (na 6px
        // beweging, PointerSensor) tonen we de sleep-cursor. Voorheen altijd 'grab',
        // waardoor de tegel als 'alleen slepen' aanvoelde i.p.v. klikbaar.
        position: 'relative', cursor: 'pointer', touchAction: 'none',
        bgcolor: T.surface, p: 2, borderRadius: '0.75rem', boxShadow: T.shadow,
        display: 'flex', alignItems: 'center', gap: 1.5, minHeight: 64,
        transition: 'box-shadow 0.15s, transform 0.15s',
        '&:hover': { boxShadow: '0 10px 30px rgba(45,51,53,0.1)' },
        '&:active': { cursor: 'grabbing' },
      }}
    >
      <ShortcutVisual item={item}faviconFailed={faviconFailed} onFaviconError={() => onFaviconError(item.id)} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{
          fontFamily: T.font, fontSize: '0.85rem', fontWeight: 700, color: T.text,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.3,
        }}>
          {item.name}
        </Typography>
        <Typography sx={{
          fontFamily: T.fontBody, fontSize: '0.7rem', color: T.outline,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {host || '—'}
        </Typography>
      </Box>
      {isHovered && (
        <Box
          onPointerDown={(e) => e.stopPropagation()}
          sx={{
            position: 'absolute', top: 4, right: 4, display: 'flex', gap: 0.25,
            bgcolor: 'rgba(255,255,255,0.95)', borderRadius: '0.4rem', p: 0.25,
          }}
        >
          <Tooltip title="Bewerken" arrow>
            <IconButton size="small"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.preventDefault(); onEdit(e, item); }}
              sx={{ p: 0.25, color: T.outline }}
            >
              <Icon name="edit" size={14} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Verwijderen" arrow>
            <IconButton size="small"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.preventDefault(); onRemove(e, item.id); }}
              sx={{ p: 0.25, color: T.outline }}
            >
              <Icon name="close" size={14} />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
}

function TilePresentation({ item, dragging }) {
  const host = hostnameOf(item.url);
  return (
    <Box sx={{
      bgcolor: T.surface, p: 2, borderRadius: '0.75rem',
      boxShadow: dragging ? '0 20px 40px rgba(45,51,53,0.18)' : T.shadow,
      display: 'flex', alignItems: 'center', gap: 1.5, minHeight: 64,
      cursor: 'grabbing',
      transform: dragging ? 'scale(1.04)' : 'none',
    }}>
      <ShortcutVisual item={item}/>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{
          fontFamily: T.font, fontSize: '0.85rem', fontWeight: 700, color: T.text,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.3,
        }}>
          {item.name}
        </Typography>
        <Typography sx={{
          fontFamily: T.fontBody, fontSize: '0.7rem', color: T.outline,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {host || '—'}
        </Typography>
      </Box>
    </Box>
  );
}

function LockedTile({ item, faviconFailed, onOpen, onFaviconError }) {
  const host = hostnameOf(item.url);

  return (
    <Box
      onClick={() => onOpen(item)}
      sx={{
        position: 'relative', cursor: 'pointer',
        bgcolor: T.surface, p: 2, borderRadius: '0.75rem', boxShadow: T.shadow,
        display: 'flex', alignItems: 'center', gap: 1.5, minHeight: 64,
        transition: 'box-shadow 0.15s, transform 0.15s',
        '&:hover': { boxShadow: '0 10px 30px rgba(45,51,53,0.1)', transform: 'translateY(-2px)' },
      }}
    >
      <ShortcutVisual item={item}faviconFailed={faviconFailed} onFaviconError={onFaviconError} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{
          fontFamily: T.font, fontSize: '0.85rem', fontWeight: 700, color: T.text,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.3,
        }}>
          {item.name}
        </Typography>
        <Typography sx={{
          fontFamily: T.fontBody, fontSize: '0.7rem', color: T.outline,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {host || '—'}
        </Typography>
      </Box>
      <Tooltip title="Standaard shortcut (beheerd door admin)" arrow placement="top">
        <Box sx={{
          position: 'absolute', top: 6, right: 6,
          color: T.outlineVariant, display: 'flex',
        }}>
          <Icon name="lock" size={12} />
        </Box>
      </Tooltip>
    </Box>
  );
}
